import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AutomatedResponsePanel = ({ interventionHistory, onExecuteIntervention }) => {
  const [selectedIntervention, setSelectedIntervention] = useState(null);

  const interventionTypes = [
    {
      id: 'database_optimization',
      name: 'Database Optimization',
      description: 'Optimize database indexes and query performance',
      icon: 'Database',
      color: 'blue',
      estimatedTime: '2-5 minutes'
    },
    {
      id: 'cache_clearing',
      name: 'Cache Clearing',
      description: 'Clear application cache to resolve stale data issues',
      icon: 'RefreshCw',
      color: 'green',
      estimatedTime: '30 seconds'
    },
    {
      id: 'service_restart',
      name: 'Service Restart',
      description: 'Restart affected services to recover from errors',
      icon: 'RotateCw',
      color: 'orange',
      estimatedTime: '1-2 minutes'
    },
    {
      id: 'load_balancing',
      name: 'Load Balancing Adjustment',
      description: 'Adjust load balancing rules to distribute traffic',
      icon: 'Activity',
      color: 'purple',
      estimatedTime: '1 minute'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100',
      green: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100',
      orange: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100',
      purple: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
    };
    return colors?.[color] || 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100';
  };

  const handleExecute = (interventionId) => {
    onExecuteIntervention(interventionId, {});
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon name="Zap" className="w-5 h-5 text-purple-600" />
          Automated Response Engine
        </h3>
        <p className="text-sm text-gray-600 mt-1">Execute pre-approved fixes for predicted issues</p>
      </div>
      {/* Intervention Options */}
      <div>
        <div className="text-sm font-medium text-gray-700 mb-3">Available Interventions</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {interventionTypes?.map(intervention => (
            <div
              key={intervention?.id}
              className={`border rounded-lg p-4 transition-all cursor-pointer ${
                getColorClasses(intervention?.color)
              }`}
              onClick={() => setSelectedIntervention(intervention)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center`}>
                    <Icon name={intervention?.icon} className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{intervention?.name}</div>
                    <div className="text-xs text-gray-600 mt-0.5">{intervention?.estimatedTime}</div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700 mb-3">{intervention?.description}</p>
              <Button
                onClick={(e) => {
                  e?.stopPropagation();
                  handleExecute(intervention?.id);
                }}
                size="sm"
                className="w-full"
              >
                <Icon name="Play" className="w-4 h-4 mr-2" />
                Execute
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Intervention History */}
      <div>
        <div className="text-sm font-medium text-gray-700 mb-3">Execution History</div>
        {interventionHistory?.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {interventionHistory?.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    item?.result?.success ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <Icon
                      name={item?.result?.success ? 'CheckCircle' : 'XCircle'}
                      className={`w-4 h-4 ${
                        item?.result?.success ? 'text-green-600' : 'text-red-600'
                      }`}
                    />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {item?.type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </div>
                    <div className="text-xs text-gray-600">{item?.result?.message}</div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(item?.timestamp)?.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <Icon name="Clock" className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No interventions executed yet</p>
          </div>
        )}
      </div>
      {/* Success Metrics */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-900">Automation Success Rate</div>
          <span className="text-2xl font-bold text-green-600">94.2%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div className="bg-green-600 h-2 rounded-full" style={{ width: '94.2%' }}></div>
        </div>
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Total Executions: 1,247</span>
          <span>Successful: 1,175</span>
        </div>
      </div>
    </div>
  );
};

export default AutomatedResponsePanel;
